/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */

// Import Core Libraries
import { Component, OnInit, Input, OnDestroy } from '@angular/core';

// Import Interfaces
import { ProductSelectorConfigData } from '@core/aem/interfaces/product-selector/product-selector.interface';
import { CartItem } from '@support/api/proxies/responses/api-cart-response.interface';

// Service
import { ProductSelectorService } from '@experience/shop/product-selector/services/product-selector.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';

// Import Constants
import {
  DEFAULT_MAX_INDEX,
  PRODUCT_SELECTOR_CONSTANTS,
} from '@experience/shop/product-selector/constants/product-selector.constants';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { LineDeivceDetails } from '@experience/shop/product-selector/interfaces/product-selector.interface';

@Component({
  selector: 'tmo-product-summary',
  templateUrl: './product-summary.component.html',
})
export class ProductSummaryComponent implements OnInit, OnDestroy {
  /**
   * Stores the AEM Content
   */
  @Input() public aemContent: ProductSelectorConfigData;
  /**
   * Stores the products array
   */
  @Input() public set nonLineItems(items: LineDeivceDetails[]) {
    this.nonLineItemsList = items;
    this.updateItemQuantity();
  }

  public nonLineItemsList: LineDeivceDetails[];
  /**
   * Store the type of product
   */
  @Input() public type: string;

  /**
   * Stores the boolean to show aal the products
   */
  public seeAll: boolean;
  /**
   * Stores the maximum number of accessories to be shown
   */
  public maxLimit: number;
  /**
   * Stores the default maximum number of accessories to be shown
   */
  public defaultMaxLimit: number;
  /**
   * Accessories added to cart description
   */
  public addedAccessoriesCountDescription: string;
  /**
   * Devices added to cart description
   */
  public addedCountDescription: string;
  /**
   * Stored the value of updated product
   */
  public updatedProduct: CartItem[];
  /**
   * Stores the Constants
   */
  public productSelectorConstant: ObjectInterface;

  constructor(
    private productSelectorService: ProductSelectorService,
    private commonUtilsService: CommonUtilService
  ) {
    this.seeAll = false;
    this.defaultMaxLimit = DEFAULT_MAX_INDEX;
  }

  public ngOnInit(): void {
    this.maxLimit = this.defaultMaxLimit;
    this.productSelectorConstant = PRODUCT_SELECTOR_CONSTANTS;
  }

  /**
   * Updates the product
   * @return void
   */
  public updateItemQuantity(): void {
    this.nonLineItemsList = this.productSelectorService.groupSimilarSkuDevices(
      this.nonLineItemsList,
      this.type
    );
    this.addedCountDescription =
      this.type === PRODUCT_SELECTOR_CONSTANTS.ACCESSORIES
        ? this.commonUtilsService.messageFormatter(this.aemContent.addedAccessoryDescription, [
            this.getTotalCount().toString(),
          ])
        : this.commonUtilsService.messageFormatter(this.aemContent.addedDeviceDescription, [
            this.getTotalCount().toString(),
          ]);
  }

  /**
   * Function to get the total number of products added to cart
   * @returns number
   */
  public getTotalCount(): number {
    let totalCount: number = 0;
    if (this.nonLineItemsList) {
      for (const product of this.nonLineItemsList) {
        if (product.quantity && product.isLineSetupCompleted) {
          totalCount += product.quantity;
        }
      }
    }
    return totalCount;
  }

  /**
   * Function to set seeAllAccessories and set the maximum number of accessories to be shown
   * @returns void
   */
  public showAll(): void {
    this.seeAll = !this.seeAll;
    this.maxLimit = this.seeAll ? this.nonLineItemsList.length : this.defaultMaxLimit;
  }

  /**
   * Destroys all the subscriptions
   */
  public ngOnDestroy(): void {}
}
